import { CANVAS_MAX_FRAME_EDGE } from '@/features/openpencil-figma/openpencil-source-canvas-layout'

export interface SafeAreaInsets {
  top: number
  right: number
  bottom: number
  left: number
}

export interface GeneratedAppViewportMetadata {
  width: number
  height: number
  safeArea?: SafeAreaInsets
  device?: 'mobile' | 'tablet' | 'desktop'
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function string(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function deviceHint(...values: Array<Record<string, unknown> | undefined>): GeneratedAppViewportMetadata['device'] {
  for (const value of values) {
    for (const candidate of [value?.device, value?.platform, value?.defaultDevice, value?.defaultViewport]) {
      if (typeof candidate !== 'string') continue
      const normalized = candidate.toLowerCase()
      if (['mobile', 'phone', 'ios', 'android'].includes(normalized)) return 'mobile'
      if (normalized === 'tablet') return 'tablet'
      if (['desktop', 'web'].includes(normalized)) return 'desktop'
    }
  }
  return undefined
}

function safeArea(value: unknown, width: number, height: number): SafeAreaInsets | undefined {
  const candidate = record(value)
  if (!candidate) return undefined
  const top = finiteNumber(candidate.top)
  const right = finiteNumber(candidate.right)
  const bottom = finiteNumber(candidate.bottom)
  const left = finiteNumber(candidate.left)
  if (
    top === undefined || right === undefined || bottom === undefined || left === undefined ||
    [top, right, bottom, left].some((entry) => entry < 0 || !Number.isInteger(entry)) ||
    left + right >= width ||
    top + bottom >= height
  ) return undefined
  return { top, right, bottom, left }
}

function viewport(value: Record<string, unknown> | undefined): GeneratedAppViewportMetadata | undefined {
  const width = finiteNumber(value?.width)
  const height = finiteNumber(value?.height)
  if (
    width === undefined || height === undefined ||
    !Number.isInteger(width) || !Number.isInteger(height) ||
    width < 1 || height < 1 ||
    width > CANVAS_MAX_FRAME_EDGE || height > CANVAS_MAX_FRAME_EDGE
  ) return undefined
  const insets = safeArea(value?.safeArea ?? value?.safeAreaInsets, width, height)
  return { width, height, ...(insets ? { safeArea: insets } : {}) }
}

/**
 * Resolve only viewport metadata written by the generated page/app/source.
 * Breakpoint fallback is intentionally not duplicated here: project open
 * already resolves settings/manifest device hints, source app kind, then the
 * canonical preset and preserves the user's per-project override.
 */
export function resolveGeneratedAppViewportMetadata(
  project: unknown,
  currentPageId: string | null | undefined,
): GeneratedAppViewportMetadata | undefined {
  const projectRecord = record(project)
  const manifest = record(projectRecord?.manifest)
  if (!manifest) return undefined

  const pages = Array.isArray(manifest.pages)
    ? manifest.pages.map(record).filter((page): page is Record<string, unknown> => Boolean(page))
    : []
  const activePage = pages.find((page) => string(page.id) === currentPageId) ?? pages[0]
  const sourceConversion = record(manifest.sourceConversion)
  const studio = record(manifest.studio)
  const candidates = [
    record(activePage?.sourceFrame),
    record(activePage?.viewport),
    record(sourceConversion?.sourceFrame),
    ...(Array.isArray(sourceConversion?.sourceFrames) ? sourceConversion.sourceFrames.map(record) : []),
    record(studio?.viewport),
  ].filter((candidate): candidate is Record<string, unknown> => Boolean(candidate))
  const selected = candidates.find((candidate) => viewport(candidate))
  if (!selected) return undefined
  const resolved = viewport(selected)
  if (!resolved) return undefined
  const device = deviceHint(selected, activePage, sourceConversion, studio) ??
    (projectRecord?.app_target === 'mobile' || sourceConversion?.mobileOnly === true
      ? 'mobile'
      : undefined)
  return { ...resolved, ...(device ? { device } : {}) }
}

export function generatedAppViewportBreakpoint(
  viewport: GeneratedAppViewportMetadata | undefined,
): 'mobile' | 'tablet' | 'desktop' | undefined {
  if (!viewport) return undefined
  if (viewport.device) return viewport.device
  if (viewport.width <= 600) return 'mobile'
  if (viewport.width <= 1024) return 'tablet'
  return 'desktop'
}

export function resolveProjectSourceViewport(
  project: unknown,
  currentPageId: string | null | undefined,
): { width: number; height: number } | undefined {
  const resolved = resolveGeneratedAppViewportMetadata(project, currentPageId)
  return resolved ? { width: resolved.width, height: resolved.height } : undefined
}
